import React, { useState } from 'react';

import logger from 'features/observability/logger';
import {
  AccessibilityInfo,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

type AgeAssuranceSharingRequiredScreenProps = {
  onRetry: () => void;
};

export const AgeAssuranceSharingRequiredScreen = ({
  onRetry,
}: AgeAssuranceSharingRequiredScreenProps) => {
  const insets = useSafeAreaInsets();
  const retryLabel = t('ageAssuranceRecheckSharing', 'Check Again');
  const helpLabel = t('ageAssuranceSharingHelp', 'Age-sharing help');
  const helpErrorLabel = t(
    'ageAssuranceSharingHelpError',
    "We couldn't open age-sharing help. Try the link again, or follow the settings steps above."
  );
  const [helpOpenFailed, setHelpOpenFailed] = useState(false);
  const isIOS = Platform.OS === 'ios';
  const helpUrl = isIOS
    ? 'https://www.apple.com/legal/privacy/data/en/age-range-for-apps/'
    : 'https://support.google.com/googleplay/answer/17232873';

  const openHelp = async () => {
    setHelpOpenFailed(false);
    try {
      await Linking.openURL(helpUrl);
    } catch {
      logger.warn({
        message: 'Age assurance provider help failed to open',
        ageAssuranceEvent: 'help_open_failed',
        ageAssuranceHelpProvider: isIOS ? 'apple' : 'google',
      });
      setHelpOpenFailed(true);
      if (isIOS) {
        AccessibilityInfo.announceForAccessibility(helpErrorLabel);
      }
    }
  };

  return (
    <ScrollView
      accessibilityViewIsModal
      contentContainerStyle={[
        styles.container,
        { paddingTop: insets.top + spacing.$6, paddingBottom: insets.bottom + spacing.$6 },
      ]}
      importantForAccessibility="yes"
      style={styles.screen}
      testID="age-assurance-sharing-required"
    >
      <View style={styles.content}>
        <Typography accessibilityRole="header" bold textAlign="center" variant="headerOne">
          {t('ageAssuranceSharingTitle', 'Share your age range to use the BK® app')}
        </Typography>
        <Typography textAlign="center">
          {t(
            'ageAssuranceSharingBody',
            'Your app store has not shared your age range with Burger King. Update your age-sharing settings using the steps below, then return to the app.'
          )}
        </Typography>
        {isIOS ? (
          <>
            <Typography>
              {t(
                'ageAssuranceSharingIOSSteps',
                'Open Settings → your name → Personal Information → Age Range for Apps → Burger King → Share Age Range Again. Follow the system instructions and enter your Screen Time passcode if requested.'
              )}
            </Typography>
            <Typography>
              {t(
                'ageAssuranceSharingIOSPreference',
                'If age sharing is set to Never, update your sharing preference in Age Range for Apps. Choosing Always also resets previous declined requests.'
              )}
            </Typography>
            <Typography>
              {t(
                'ageAssuranceSharingIOSFamilySteps',
                'For a child or teen in a family group, a parent or guardian may need to open Settings → Family → the child’s name → Personal Information → Age Range for Apps to manage sharing.'
              )}
            </Typography>
          </>
        ) : (
          <>
            <Typography>
              {t(
                'ageAssuranceSharingAndroidSteps',
                'Open Google Play → Burger King’s app details → the three-dot menu → turn on Share age range. This option is available when your account and global sharing settings permit it.'
              )}
            </Typography>
            <Typography>
              {t(
                'ageAssuranceSharingAndroidPreference',
                'To manage global sharing, open Google Play → your profile → Settings → Family → Age range sharing. Always share includes apps you previously declined to share with.'
              )}
            </Typography>
            <Typography>
              {t(
                'ageAssuranceSharingAndroidFamilySteps',
                'For a supervised account, a parent or guardian must open Family Link → select the child → Google Play → Age sharing → Share age range with all apps.'
              )}
            </Typography>
            <Typography>
              {t(
                'ageAssuranceSharingAndroidAvailability',
                'Google manages age-sharing availability. If these settings are missing for your account or region, see Google’s age-sharing help below.'
              )}
            </Typography>
          </>
        )}
        <Typography textAlign="center">
          {t(
            'ageAssuranceSharingRecheckHelp',
            'These changes must be made through your app store or system settings. Burger King cannot reset your sharing choice. Once you have updated it, return here and select Check Again, or relaunch the app. A new system prompt may not appear.'
          )}
        </Typography>
        <Pressable
          accessibilityLabel={retryLabel}
          accessibilityRole="button"
          onPress={onRetry}
          style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
          testID="age-assurance-recheck-sharing"
        >
          <Typography bold color={primitive.$white}>
            {retryLabel}
          </Typography>
        </Pressable>
        <Typography
          accessibilityRole="link"
          onPress={openHelp}
          testID="age-assurance-sharing-help"
          underline
        >
          {helpLabel}
        </Typography>
        {helpOpenFailed ? (
          <Typography
            accessibilityLiveRegion="polite"
            accessibilityRole="alert"
            color={primitive.$fieryRed}
            textAlign="center"
          >
            {helpErrorLabel}
          </Typography>
        ) : null}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: {
    backgroundColor: primitive.$mayoEggWhite,
    flex: 1,
  },
  container: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.$6,
  },
  content: {
    alignItems: 'center',
    gap: spacing.$5,
    maxWidth: 480,
    width: '100%',
  },
  button: {
    alignItems: 'center',
    backgroundColor: primitive.$fieryRed,
    borderCurve: 'continuous',
    borderRadius: 9999,
    justifyContent: 'center',
    minHeight: spacing.$12,
    paddingHorizontal: spacing.$6,
    width: '100%',
  },
  buttonPressed: {
    backgroundColor: primitive.$fieryRedPressed,
  },
});
